import { useEffect, useState } from 'react';
import { useEditor } from './store.ts';

export const APP_VERSION = __APP_VERSION__;
export const APP_BUILT = __APP_BUILT__;

const BUNDLE = /\/assets\/index-[\w-]+\.js/;

/** true when the deployed index.html points at a different bundle than the one running */
async function newVersionDeployed(): Promise<boolean> {
  const running = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute('src');
  if (!running) return false;
  try {
    const html = await (await fetch('/', { cache: 'no-store' })).text();
    const live = html.match(BUNDLE)?.[0];
    return !!live && !running.endsWith(live);
  } catch {
    return false;
  }
}

/** checks for a newer deploy on start, every few minutes and whenever the app comes back into view */
export function useUpdateAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let stopped = false;
    const check = () => {
      if (document.visibilityState === 'visible') newVersionDeployed().then((v) => !stopped && v && setAvailable(true));
    };
    check();
    const timer = setInterval(check, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  return available;
}

/**
 * Drop the service worker and its caches, re-download the non-hashed files past the
 * HTTP cache (index.html, game data), then reload. Hashed /assets/ bundles change name per build.
 */
export async function hardRefresh(save: () => Promise<void>) {
  const s = useEditor.getState();
  if (s.dirty && s.doc) {
    if (!confirm('You have unsaved changes. Save them and load the latest version?')) return;
    await save();
  }
  s.notify('Downloading the latest version…');
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    if ('caches' in window) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    const urls = new Set(['/', '/manifest.webmanifest']);
    for (const e of performance.getEntriesByType('resource')) {
      const u = new URL(e.name);
      if (u.origin === location.origin && u.pathname.startsWith('/game-data/')) urls.add(u.pathname + u.search);
    }
    await Promise.allSettled([...urls].map((u) => fetch(u, { cache: 'reload' })));
  } finally {
    location.reload();
  }
}
